<template>
  <GamMainView :class="['notifications-view', { 'is-mobile': isMobile }]">
    <div class="head">
      <Vue3PullToRefresh
        :distance="50"
        :duration="300"
        :size="32"
        noreload
        :options="{ color: '#CCC', bgColor: '#282828' }"
        @onrefresh="notificationStore.fetchAll"
      />

      <div class="title-row">
        <span class="heading">
          <span class="news-for">{{ localize('news.short_title') }}</span>
        </span>

        <div class="actions">
          <GamButton
            v-if="unreadCount"
            :size="GamButtonSize.SMALL"
            :variant="GamButtonVariant.AFFORDED"
            :is-disabled="isSubmitting"
            @click="notificationStore.markAllAsRead"
          >
            {{ localize('news.action.read') }}
          </GamButton>

          <GamButton
            v-else-if="notifications.length"
            :size="GamButtonSize.SMALL"
            :variant="GamButtonVariant.AFFORDED"
            :is-disabled="isSubmitting"
            @click="notificationStore.deleteAll"
          >
            {{ localize('news.action.clear') }}
          </GamButton>

          <GamButton
            v-if="needsPermission"
            :size="GamButtonSize.SMALL"
            :variant="GamButtonVariant.SECONDARY"
            :is-disabled="isSubmitting"
            :is-loading="isEnablingPush"
            @click="onNotificationsEnable"
          >
            {{ localize('news.action.short_notifications') }}
          </GamButton>
        </div>
      </div>

      <template v-if="false">
        <GamInputSearch v-bind="getSearch()" class="search" />
        <GamTab class="tabs" :tabs="listTabItems" :size="GamTabSize.MEDIUM" @update="onTabItemClick" />
      </template>
    </div>

    <div class="entries">
      <GamSimpleListView id="notification-list" v-bind="getNotificationListProps">
        <template #list>
          <template v-if="notifications.length">
            <NotificationEntry
              class="notification"
              v-for="notification in notifications"
              :notification="notification"
              :key="`notif-${notification.id}`"
            />
          </template>
          <template v-else-if="isLoading">
            <div class="skeleton-loader">
              <div class="skeleton-item skeleton o-2" />
              <div class="skeleton-item skeleton o-3" />
              <div class="skeleton-item skeleton o-4" />
            </div>
          </template>
        </template>
      </GamSimpleListView>
    </div>

    <div class="nav-close-btn" @click="handleClose">
      <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
    </div>
  </GamMainView>
</template>

<script lang="ts" setup>
import type { GetTableParams } from '@/core/network/http/httpClient.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useFilterStore } from '@/stores/filter.store';
import { useNotificationStore } from '@/stores/notification.store';
import GamMainView from '@/views/components/GamMainView.vue';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';

import { RouterNameType, TabItemType } from '@/core/data/config/uiConfig.interface';
import type { NotificationDto } from '@/core/data/notification/notification.interface';
import { isFirebasePushConfigured, isPushPermissionGranted } from '@/core/domain/notification/pushNotifications';
import { localize } from '@/core/gambits';
import { listInfo } from '@/core/helpers/list.helper';
import { areNotificationsSupported, isMobile } from '@/core/helpers/ui.helper';
import GamSimpleListView from '@/views/components/gam-list-view/GamSimpleListView.vue';
import GamTab from '@/views/components/gam-tab/GamTab.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamInputSearch from '@/views/components/GamInputSearch.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamTabSize } from '@/views/composables/constants/components/gamTab.constants';
import type { GamInputSearchType } from '@/views/composables/models/components/GamInput';
import type { GamSimpleIntersectType } from '@/views/composables/models/components/GamSimpleIntersect';
import type { GamTabItemType } from '@/views/composables/models/components/GamTab';
//@ts-ignore
import Vue3PullToRefresh from '@amirafa/vue3-pull-to-refresh';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import NotificationEntry from './NotificationEntry.vue';

const listId = GamListId.NOTIFICATIONS;
const filterStore = useFilterStore<GetTableParams>(listId)();
const { listTabItems } = storeToRefs(filterStore);

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const notificationStore = useNotificationStore();
const { notifications, unreadCount, isSubmitting, isLoading, afterCursor } = storeToRefs(notificationStore);

const router = useRouter();
const route = useRoute();

const handleClose = () => {
  if (route.query.back === 'history') {
    router.back();
  } else if (userDetails.value?.username) {
    router.push({ name: RouterNameType.USERNAME, params: { username: userDetails.value.username } });
  } else {
    router.push({ name: RouterNameType.LOCATIONS });
  }
};

const notificationTabs: GamTabItemType[] = [
  {
    name: TabItemType.ALL,
    displayName: 'All notifications',
    position: 0,
    isActive: true,
  },
  {
    name: TabItemType.PARTICIPATING,
    displayName: 'Participating',
    position: 1,
  },
  {
    name: TabItemType.FOLLOWING,
    displayName: 'Following',
    position: 2,
  },
];

const onTabItemClick = async (name: TabItemType) => {
  filterStore.setActiveTab(name);
};

onBeforeMount(() => {
  filterStore.setListTabItems(notificationTabs);
});

onMounted(async () => {
  notificationStore.setCursor(null, null);
  await notificationStore.fetchAll();
});

const needsPermission = computed(
  () =>
    isFirebasePushConfigured() &&
    areNotificationsSupported.value &&
    (!userDetails.value?.notificationPreferences?.push || !isPushPermissionGranted()),
);

const isEnablingPush = ref(false);

const onNotificationsEnable = async () => {
  isEnablingPush.value = true;
  try {
    const ok = await notificationStore.enablePushNotifications();

    if (ok) {
      useToast().success(localize('news.push.enabled'));
    } else {
      useToast().error(localize('news.push.enable_failed'));
    }
  } finally {
    isEnablingPush.value = false;
  }
};

const getSearch = (): GamInputSearchType<NotificationDto> => ({
  id: 'search',
  isSearch: true,
  input: {
    name: 'search',
    label: 'Search',
    placeholder: 'Search for notifications',
    leftIcon: GamIconName.SEARCH,
  },
});

const getNotificationListProps = computed(
  (): GamSimpleIntersectType => ({
    loadMore: notificationStore.fetchMore,
    isLoading: isLoading.value,
    selectFirst: false,
    showMore: !!afterCursor.value,
    isEmpty: notifications.value.length === 0 && !isLoading.value,
    noDataInfo: noDataInfo,
    scrollSpacing: true,
    showFooter: false,
    spacerPadding: isMobile.value ? 88 : 114,
    footerPadding: isMobile.value ? 96 : 120,
  }),
);

const noDataInfo = listInfo({
  id: GamListId.NOTIFICATIONS,
  title: '',
  message: localize('notifications.list.empty.message'),
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.nav-close-btn {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

.main-view {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;
  background: var(--color-linear-gradient-vertical) var(--color-dark-700);
}

.head {
  position: absolute;
  padding-bottom: var(--spacing-s);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  right: 0;
  background: transparent;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  padding-top: calc(var(--spacing-xl) + var(--spacing-xs));
  gap: var(--spacing-md);
  z-index: 2;
}

.notifications-view:not(.is-mobile) .head {
  padding-bottom: calc(var(--spacing-xl) + var(--spacing-xs));
}

.notifications-view.is-mobile .head {
  padding-top: calc(var(--spacing-l) * 2);
  gap: var(--spacing-l);
  padding-left: var(--spacing-l);
  padding-right: var(--spacing-l);
}

.title-row {
  max-width: 600px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--spacing-l);
}

.heading {
  font-size: 32px;
  font-weight: 700;
  line-height: 110%; /* 35.2px */
  color: var(--color-white-50);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.25em;
  box-shadow: var(--shadow-small);
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-md);
}

.is-mobile .actions {
  flex-grow: 1;
  justify-content: flex-end;
}

.search,
.tabs {
  width: 100%;
  max-width: 600px;
}

.search {
  align-self: unset;
}

.scroll {
  width: 100%;
  height: 100%;
}

.entries {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-s);

  .no-data {
    padding-top: var(--spacing-xxl);
  }
}

.notification,
.skeleton-item {
  align-self: center;
}

.skeleton-item {
  max-width: 600px;
  height: 152.19px;
}

.notifications-view :deep(.gam-list-view:after) {
  display: none;
}
</style>
